/**
 * MONSTER TOWN — GOING TO BED. TIME-OF-DAY CREW (src/time/**).
 *
 * `clock.sleepToMorning()` is instant and synchronous — one frame it is half
 * past nine at night, the next it is seven in the morning. That is correct for
 * the clock and it is a terrible thing to watch, and it also throws away the
 * single most magical moment the cycle has to offer.
 *
 * So the bed gets a SHOW: the picture closes in like a pair of eyelids, the sky
 * turns (the whole star field wheels around its pole, which is the night passing
 * in three seconds), and then the sun comes up on the new day. Three seconds and
 * one bit long, in three beats.
 *
 * LAW #2 IS NOT BENT HERE. The overlay is `pointer-events: none` from the first
 * line: a kid can tap straight through it the entire time, walk off, open the
 * shop, do anything. Nothing is blocked, nothing is disabled, and nothing is
 * lost if the show is ignored — it is scenery, not a modal. Missing it is
 * impossible because it cannot be failed.
 *
 * NO TEXT (LAW #4). One coloured pane and the sky. That is the whole UI.
 */

import { bus } from '../core/events'
import { clock } from '../core/time'
import { save } from '../core/save'
import { getPlaceable } from '../core/placeable'
import { hourToT, MORNING_HOUR } from '../core/time'
import { blendHex, cssHex, lookAt } from './look'
import { hex } from '../core/palette'
import { dreamSheetUrl, SKY_POLE } from './sky'

// ---------------------------------------------------------------------------
// the timeline
// ---------------------------------------------------------------------------

/** Eyelids closing. */
const SETTLE_MS = 700
/** The night going by. */
const WHEEL_MS = 1200
/** The sun coming up. */
const RISE_MS = 1200
const TOTAL_MS = SETTLE_MS + WHEEL_MS + RISE_MS

/** Where the scripted sky goes: late evening -> the small hours -> morning. */
const T_DEEP_NIGHT = 0.9
const T_SMALL_HOURS = 1.14
const T_FIRST_LIGHT = 0.205
const T_MORNING = hourToT(7)

/** How far the sky turns. About a turn and a half — a night, hurried along. */
const WHEEL_TURNS = 1.55
/**
 * The wheel is QUANTISED, and that is a performance decision, not a look one:
 * every distinct angle costs one repaint and one texture upload, so the sky is
 * stepped ~0.24 rad at a time. At this speed the steps are far below what the
 * eye separates from motion blur, and it holds the whole show under ~40 uploads.
 */
const WHEEL_STEP = 0.24
/**
 * How far the world goes away. Not 1: a kid should still be able to see their
 * own town underneath, dim, the way a room looks when the light goes off — and
 * the tap they make during the fade should land on something they can see.
 */
const PEAK_ALPHA = 0.82

const INK = hex('ink')

/**
 * DEEP BLUE-PLUM, and it has to be MIXED IN rather than taken from the sky.
 *
 * The pane used to be the small hours' own `skyTop` straight, which is the
 * palette's `shadow` more than half way to `ink` — a colour so close to neutral
 * that once the world underneath it was being graded correctly (see the
 * premultiplied-alpha note in `tint.ts`; before that fix the whole town WAS a
 * flat lilac slab and the fade inherited its colour by accident), the fade came
 * out a muddy brown-grey. A dream is the one moment in the game that gets to be
 * frankly violet, and a white star needs a coloured sky to sit on.
 *
 * ROUND 2: IT WAS STILL NOT VIOLET ENOUGH, AND IT WAS TOO LIGHT. The pane sits
 * at 0.82, so nearly a fifth of a GREEN town comes through it — and a
 * barely-violet pane plus a fifth of grass is the *"uniform olive-grey haze"*
 * the critic photographed at `sleep-1400ms.png`. It is now mixed from a base
 * that is half way to `ink` and taken further toward the lilac: (137,124,181),
 * deeper AND with the green channel below the red, so the same composite lands
 * plainly violet. The gate measures it — blue must beat green in the slit.
 */
const PLUM = blendHex(blendHex(hex('shadow'), hex('ink'), 0.5), hex('monsterLilac'), 0.62)

/**
 * THE SUN COMING UP, for the last beat. `look.skyTop` on the way out of the
 * night is a pale lilac, and a pale lilac pane at 48% over a bright morning town
 * is *"a milky pink-white wash… the lowest-contrast image in the day"* — exactly
 * what `sleep-2400ms.png` is. The wake-up is light FLOODING IN, so the pane goes
 * warm gold on the way out instead of pale, and it clears faster (see `RISE_EASE`).
 */
const SUNRISE = blendHex(hex('sunWarm'), hex('roofMustard'), 0.34)

/**
 * THE DREAM'S OWN SKY, HELD STILL — one constant, not `look.skyTop`.
 *
 * The pane used to take its base from whatever hour the show was CURRENTLY
 * painting, and the show runs the clock from late evening through the small
 * hours to first light. At 1900 ms it is showing ~04:55, whose `skyTop` is
 * already a light blue (136,155,206) — and the pane is still at its full 0.82,
 * so the frame came out as a pale lilac haze over the whole town while the
 * eyelids were still shut. Measured, that made 1900 ms the second flattest frame
 * of the fade for exactly the same reason 2400 ms was the flattest.
 *
 * A dream is not a weather report. The pane holds ONE deep-night violet for the
 * whole show and only `rise` moves it — toward the sunrise, at the moment the
 * eyes are opening. The WORLD underneath still runs the whole night; that is
 * what the star wheel is for.
 */
const DREAM_SKY = lookAt(T_DEEP_NIGHT).skyTop
/**
 * How fast the pane gets out of the way once the eyes are opening. >1 means it
 * is mostly gone by the time the town is bright, so the last thing the fade does
 * is hand the kid a crisp morning rather than a milky one.
 */
const RISE_EASE = 2.1

/**
 * HOW FAR THE EYELIDS COME DOWN, as a fraction of the glass, each.
 *
 * Critic round 1: "the promised 'closes in like eyelids' is not readable — at
 * 200 ms there is a thin dark band top and bottom, and by 1100 ms it is a flat
 * uniform veil". Both true. The bands were only ever the dark rim of a radial
 * gradient, so as the pane came up they were swallowed by their own middle.
 *
 * They are now two real bands that SLIDE, on `transform: scaleY` — a
 * compositor-only property, so a full-screen animated lid costs no repaint. They
 * stop at 34%, leaving a wide letterbox slit, because the star field wheeling
 * through it is the whole point of the fade and a shut eye cannot see it.
 *
 * ROUND 2: THEY WERE STILL NOT VISIBLE, AND THE REASON IS STRUCTURAL. The lids
 * were CHILDREN of the pane, and CSS opacity multiplies down the tree — so a
 * band of `ink` inside a pane at 0.82 came out at 0.82 of ink over a pane that
 * was itself a dark violet, i.e. two dark things within a few per cent of each
 * other. There was no edge for an eye to catch, which is precisely the critic's
 * *"there is no legible eyelid"*.
 *
 * The lids are now SIBLINGS of the pane at full opacity. `ink` (43,36,56)
 * against the pane's lit middle (~115,112,158) is a 2.5x step with a hard top
 * edge, at every frame of the close and every frame of the open.
 */
const LID_SHARE = 0.34

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
const smooth = (v: number): number => {
  const k = clamp01(v)
  return k * k * (3 - 2 * k)
}
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export interface SleepShow {
  /** True while the show is on. */
  readonly active: boolean
  /** The day fraction the WORLD should look like, overriding the clock. */
  readonly t: number | null
  /** Radians the star field is turned by. */
  readonly wheel: number
  /** 0..1 multiplier for the ambience bed — the world goes quiet under it. */
  readonly quiet: number
  /** How far down the pane is, 0..1. The HUD steps back by the same amount. */
  readonly alpha: number
}

const IDLE: SleepShow = { active: false, t: null, wheel: 0, quiet: 1, alpha: 0 }

let elapsed = -1
let fromT = 0
let overlay: HTMLDivElement | null = null
let pane: HTMLDivElement | null = null
let starLayer: HTMLDivElement | null = null
let lidTop: HTMLDivElement | null = null
let lidBottom: HTMLDivElement | null = null
let shows = 0
/** QA only: freeze the show at a fixed point so a capture can be taken of it. */
let holdAt: number | null = null

function ensureOverlay(): HTMLDivElement | null {
  if (overlay !== null) return overlay
  if (typeof document === 'undefined') return null
  const el = document.createElement('div')
  el.id = 'mt-sleep'
  el.setAttribute('aria-hidden', 'true')
  // Inline, not in style.css: that file is architect-owned and this element is
  // entirely ours. Nothing here can ever swallow a tap.
  //
  // THE CONTAINER IS ALWAYS AT FULL OPACITY. Only the SKY inside it fades. That
  // is what lets the eyelids be genuinely opaque instead of 82% of themselves —
  // see LID_SHARE. It is invisible when idle because the pane is at 0 and the
  // lids are at scaleY(0).
  el.style.cssText = [
    'position:fixed',
    'inset:0',
    'pointer-events:none',
    'z-index:60',
    'overflow:hidden',
  ].join(';')

  /** The dream sky: the graded pane, and the star field that turns on it. */
  const sky = document.createElement('div')
  sky.id = 'mt-sleep-pane'
  sky.style.cssText = [
    'position:absolute',
    'inset:0',
    'opacity:0',
    'pointer-events:none',
    'overflow:hidden',
    'will-change:opacity',
  ].join(';')
  el.appendChild(sky)
  pane = sky

  /**
   * THE STARS, AND THE ONE MOMENT THE GAME CAN ACTUALLY SHOW THEM.
   *
   * MEASURED, not assumed: the town's tree line and hills fill the top of the
   * frame at every one of the camera's four zoom steps, so the painted sky
   * behind them is almost never on screen while a kid is playing (the captures
   * in shots/time/ show it — the top row of every town frame is greenery, at
   * noon exactly as at midnight). The bed fade is different: for three seconds
   * it owns the entire glass, and it is also the moment "a night went by" has to
   * be told. So the star field is drawn HERE, over everything, and it turns.
   *
   * It is the same deterministic field the backdrop paints, from the same seed
   * and in the same draw order, exported as an image from `sky.ts` — so the two
   * can never disagree about where a star is.
   *
   * ROUND 2: IT WAS A DUSTING, NOT A SKY. *"the stars are sparse white specks
   * that read as dust or noise rather than a sky."* Counted rather than argued:
   * the field is 150 stars across a square sheet, this layer is `inset: -48%`
   * (so it is about twice the glass in each direction to survive the rotation),
   * and `background-size: 100% 100%` therefore puts roughly a QUARTER of those
   * 150 on screen — about 37 specks over an iPad. `dreamSheetUrl()` is the same
   * field with the count and the star radius raised for the one moment the game
   * actually shows a sky; the first 150 are bit-identical to the backdrop's.
   */
  const stars = document.createElement('div')
  stars.id = 'mt-sleep-stars'
  stars.style.cssText = [
    'position:absolute',
    // Oversized so a rotation never swings an empty corner into frame, and so
    // the stars come out about twice the size they are in the 256px sheet.
    'inset:-48%',
    'background-repeat:no-repeat',
    'background-size:100% 100%',
    `transform-origin:${SKY_POLE.x * 100}% ${SKY_POLE.y * 100}%`,
    'opacity:0',
    'pointer-events:none',
    'will-change:transform,opacity',
  ].join(';')
  sky.appendChild(stars)
  starLayer = stars

  /**
   * THE EYELIDS. Two bands over the stars, each a soft-edged wedge of `ink`
   * that slides in from its own side. `transform-origin` plus `scaleY` means
   * the whole animation is one compositor transform per frame and never a
   * repaint, which is what lets a full-screen lid run on the hand-me-down.
   *
   * APPENDED TO THE CONTAINER, NOT TO THE PANE, and that is the round-2 fix:
   * outside the pane's opacity they are FULLY opaque `ink` at every frame, so
   * there is a hard 2.5x step where a lid meets the sky. See LID_SHARE.
   */
  const lidCss = (from: 'top' | 'bottom'): string =>
    [
      'position:absolute',
      'left:-2%',
      'right:-2%',
      `${from}:0`,
      `height:${(LID_SHARE * 100).toFixed(0)}%`,
      // Solid for three quarters of the band, then a soft edge. At 62% the
      // dark part was only a fifth of the glass and the shape read as a haze
      // rather than as a lid coming down.
      `background:linear-gradient(to ${from === 'top' ? 'bottom' : 'top'}, ${cssHex(INK)} 0%, ${cssHex(INK)} 76%, transparent 100%)`,
      `transform-origin:${from} center`,
      'transform:scaleY(0)',
      'pointer-events:none',
      'will-change:transform',
    ].join(';')
  const top = document.createElement('div')
  top.id = 'mt-sleep-lid-top'
  top.style.cssText = lidCss('top')
  const bottom = document.createElement('div')
  bottom.id = 'mt-sleep-lid-bottom'
  bottom.style.cssText = lidCss('bottom')
  el.appendChild(top)
  el.appendChild(bottom)
  lidTop = top
  lidBottom = bottom

  /**
   * ============ WHY IT GOES INSIDE #app, IN FRONT OF THE CANVAS BUT
   *              BEHIND #hud, AND NOT ON THE BODY ============
   *
   * Critic round 1: *"the star field paints OVER the HUD panel — stars at
   * (270,240) and (250,600) are sitting on top of the control slab. Stars
   * sprinkled across a settings panel."* True, and structural: `#app` is
   * `position: fixed`, which creates a stacking context all by itself, so a
   * body-level element at ANY positive z-index paints over the whole HUD no
   * matter what z-index the HUD's own layers carry. Turning the number down
   * would not have moved it one pixel.
   *
   * Appended into `#app` immediately before `#hud`, it is in front of the
   * canvas (DOM order) and behind every HUD layer (DOM order again — `#hud` and
   * its z-indexed children both come later). The dream is now in the picture,
   * where it belongs, and the HUD sits on the glass in front of it, stepped
   * right back by `hudNight.ts` so it is not the biggest object in a
   * close-your-eyes moment.
   *
   * Geometry is unchanged: `position: fixed` is viewport-relative wherever it
   * is parented, and `#app` is itself `inset: 0`.
   */
  const app = document.getElementById('app')
  const hudEl = document.getElementById('hud')
  if (app !== null && hudEl !== null && hudEl.parentElement === app) app.insertBefore(el, hudEl)
  else document.body.appendChild(el)
  overlay = el
  return el
}

function paintOverlay(
  alpha: number,
  wheel: number,
  starness: number,
  lid: number,
  rise = 0,
): void {
  const el = ensureOverlay()
  if (el === null) return
  if (lidTop !== null) lidTop.style.transform = `scaleY(${lid.toFixed(3)})`
  if (lidBottom !== null) lidBottom.style.transform = `scaleY(${lid.toFixed(3)})`
  const sky = pane
  if (sky === null) return
  if (alpha <= 0.001) {
    sky.style.opacity = '0'
    if (starLayer !== null) starLayer.style.opacity = '0'
    return
  }
  /**
   * BOTH STOPS COME OFF THE NIGHT SKY, AND BOTH ARE DARK.
   *
   * The first version put the sky's HORIZON colour in the middle — a pale lilac
   * — and the fade came out as a bright lavender fog with an invisible star
   * field on top of it (shots/time/13-bed-stars-wheel.png, first pass: the stars
   * were at full opacity and could not be seen at all). A night sky is dark in
   * the middle and darker at the rim; that is also what lets a small white star
   * read against it.
   *
   * Both come off `DREAM_SKY`, which is HELD STILL for the whole show (see the
   * note there), and `rise` turns both toward SUNRISE over the last beat — the
   * wake-up is light coming in, not a wash draining out.
   */
  const night = blendHex(DREAM_SKY, PLUM, 0.62)
  const middle = cssHex(blendHex(night, SUNRISE, rise * 0.85))
  const rim = blendHex(DREAM_SKY, INK, 0.55)
  const edge = cssHex(blendHex(rim, SUNRISE, rise * 0.5))
  sky.style.background = `radial-gradient(circle at 50% 46%, ${middle} 0%, ${edge} 82%)`
  sky.style.opacity = String(clamp01(alpha))
  if (starLayer !== null) {
    if (starLayer.style.backgroundImage === '') {
      starLayer.style.backgroundImage = `url(${dreamSheetUrl()})`
    }
    starLayer.style.transform = `rotate(${(wheel * 180) / Math.PI}deg)`
    starLayer.style.opacity = String(clamp01(starness))
  }
}

/**
 * THE SHOW. Started by `time:slept`, whoever caused it.
 *
 * THE CLOCK HOLDS STILL WHILE IT PLAYS, for the same reason it holds still for a
 * scene transition (`core/time.ts` does that on `scene:transitionStart`): this is
 * a cut, not a stretch of the kid's day. Without the hold, "the bed takes you to
 * the morning" means *seven o'clock plus however long the fade took to render* —
 * three seconds on a quick machine, twelve on the hand-me-down iPad the game is
 * actually for, and that difference is visible in the sky the kid wakes up under.
 * `pause()` is a Set keyed by reason, so starting a second show mid-show is safe,
 * and both exits below release it.
 */
function startShow(): void {
  fromT = clock.t
  elapsed = 0
  shows++
  clock.pause(SHOW_PAUSE)
  bus.emit('audio:cue', { cue: 'bed_sleep', volume: 0.8 })
}

/** The one reason string. Released on every path out of the show. */
const SHOW_PAUSE = 'sleepShow'

/**
 * Advance the show. Called once per frame from the time system, which then uses
 * the returned `t` INSTEAD of the clock's.
 */
export function updateSleepShow(frameMs: number): SleepShow {
  if (elapsed < 0) return IDLE
  const before = elapsed
  elapsed = holdAt === null ? elapsed + frameMs : holdAt
  if (elapsed >= TOTAL_MS) {
    elapsed = -1
    clock.resume(SHOW_PAUSE)
    paintOverlay(0, 0, 0, 0, 0)
    return IDLE
  }

  let t: number
  let alpha: number
  let wheel: number
  let quiet: number
  let starness: number
  let lid: number
  let rise = 0

  if (elapsed < SETTLE_MS) {
    const k = smooth(elapsed / SETTLE_MS)
    t = lerp(fromT, T_DEEP_NIGHT, k)
    alpha = PEAK_ALPHA * k
    wheel = 0
    quiet = lerp(1, 0.35, k)
    starness = k
    lid = k
  } else if (elapsed < SETTLE_MS + WHEEL_MS) {
    const k = (elapsed - SETTLE_MS) / WHEEL_MS
    t = lerp(T_DEEP_NIGHT, T_SMALL_HOURS, smooth(k))
    alpha = PEAK_ALPHA
    wheel = smooth(k) * WHEEL_TURNS * Math.PI * 2 * 0.72
    quiet = 0.35
    starness = 1
    lid = 1
    if (before < SETTLE_MS + WHEEL_MS * 0.5 && elapsed >= SETTLE_MS + WHEEL_MS * 0.5) {
      bus.emit('audio:cue', { cue: 'sparkle_soft', volume: 0.4 })
    }
  } else {
    const k = (elapsed - SETTLE_MS - WHEEL_MS) / RISE_MS
    const s = smooth(k)
    t = lerp(T_FIRST_LIGHT, T_MORNING, s)
    // ROUND 2: `PEAK_ALPHA * (1 - s)` left a 48% pane over a bright morning at
    // 2400 ms, which is the milky low-contrast wake-up the critic measured.
    // The pane now gets out of the way faster than the sun comes up.
    alpha = PEAK_ALPHA * Math.pow(1 - s, RISE_EASE)
    rise = s
    // The turn eases out rather than stopping dead: the sky is settling, not
    // being switched off.
    wheel = WHEEL_TURNS * Math.PI * 2 * (0.72 + 0.28 * smooth(k))
    quiet = lerp(0.35, 1, s)
    starness = 1 - s
    // The eyes open again a beat AHEAD of the pane, so the last thing the fade
    // does is show the kid the new morning rather than a dark letterbox of it.
    lid = 1 - smooth(Math.min(1, k * 1.35))
    if (before <= SETTLE_MS + WHEEL_MS && elapsed > SETTLE_MS + WHEEL_MS) {
      bus.emit('audio:cue', { cue: 'bird', volume: 0.55 })
    }
    if (before < TOTAL_MS - 260 && elapsed >= TOTAL_MS - 260) {
      bus.emit('audio:cue', { cue: 'chime_happy', volume: 0.6 })
    }
  }

  paintOverlay(alpha, wheel, starness, lid, rise)
  return {
    active: true,
    t,
    wheel: Math.round(wheel / WHEEL_STEP) * WHEEL_STEP,
    quiet,
    alpha: alpha / PEAK_ALPHA,
  }
}

/** Diagnostics: how many times the bed fade has run this session. */
export function sleepShowCount(): number {
  return shows
}

/** Test hook — the show, without needing a bed to exist. */
export function forceSleepShow(): void {
  startShow()
}

/**
 * QA ONLY — freeze the show at a point on its timeline, or `null` to let it run.
 *
 * A screenshot on a software-GL headless browser can take seconds, and the show
 * is three; without this a tool aiming at "the middle of the star wheel" reliably
 * photographs the morning after. No kid-reachable path calls this.
 */
export function holdSleepShow(ms: number | null): void {
  holdAt = ms === null ? null : Math.max(0, Math.min(TOTAL_MS - 1, ms))
}

// ---------------------------------------------------------------------------
// the bed
// ---------------------------------------------------------------------------

/**
 * THE BED IS THE KID'S CONTROL OVER TIME, so the bed has to actually do it.
 *
 * `bed` is a placeable in `src/content/furniture.ts` (category `'bed'`) and it
 * already reacts beautifully — the monster sleeps, the blanket squashes, zzz
 * particles, a snore. What it has never done is move the clock, because until
 * this expansion there was no clock to move. The interiors crew owns that def
 * and we do not touch it, so the wire is on the bus: any placeable whose
 * category is `bed` being used means bedtime.
 *
 * IT CANNOT DOUBLE UP. The call is deferred by one task, and skipped if a
 * `time:slept` has just been seen. So on the day the interiors crew wires the
 * bed themselves — synchronously, inside the same `play:interacted` emit — the
 * clock has already moved by the time this runs and this does nothing. When
 * that lands, DELETE this block; it is a bridge, not a home.
 */
let lastSleptAt = -1e9
let bridgeEnabled = true

export function setBedBridge(on: boolean): void {
  bridgeEnabled = on
}

/**
 * ============ A NAP IS NOT A NIGHT ============
 *
 * MEASURED, critic round 1: tapping the bed at 09:01 on day 2 landed on 07:08
 * on day 3. Twenty-two in-world hours gone for ONE TAP on the most tappable
 * object in the bedroom — the daylight, the neighbours' whole routine and the
 * day counter all reset, with no undo and no confirmation.
 *
 * It does not break LAW #2 (nothing is permanently lost: the bag, the coins and
 * a running errand's step index all survive a sleep, and that was verified
 * separately). It is still the wrong thing to do to a three-year-old who taps
 * everything, and the two obvious fixes are both forbidden — a confirmation
 * card is a question a pre-reader cannot answer (LAW #4) and "are you sure?" is
 * exactly the flavour of fail state LAW #2 exists to keep out.
 *
 * So the bed does what a bed does: IT DEPENDS WHAT TIME IT IS.
 *
 *   evening / night (>= 17:00)  the night goes by, morning of the NEXT day
 *   the small hours (< 07:00)   the night is nearly over already — the bed
 *                               lands on THIS morning, not tomorrow's. Sleeping
 *                               at 3am used to cost twenty-eight hours.
 *   the day (07:00 - 17:00)     a nap. The clock does not move at all.
 *
 * A nap is NOT a dead tap, and LAW #1 is untouched by any of this: the bed's own
 * reaction is the interiors crew's and still fires in full — the monster climbs
 * in, the blanket squashes, the zzz particles go up, it snores. All that changes
 * is that a nap at nine in the morning does not throw the morning away.
 *
 * `clock.sleepToMorning()` always rolls the day (it is frozen core and its
 * header says so on purpose), so the small-hours case sets the hour directly and
 * announces the sleep itself. `time:slept` means "a sleep happened" — the show,
 * the routine reset and the well-rested bonus all hang off it, and all three are
 * exactly as right at six in the morning as at ten at night.
 */
const BEDTIME_HOUR = 17

function bedTapped(): void {
  const from = clock.now()
  if (from.hour >= BEDTIME_HOUR) {
    clock.sleepToMorning()
    return
  }
  if (from.hour < MORNING_HOUR) {
    const to = clock.setHour(MORNING_HOUR)
    bus.emit('time:slept', { fromHour: from.hour, toHour: to.hour, day: to.day })
    return
  }
  // Broad daylight: a nap. Nothing to do — and deliberately nothing undone.
}

export function installSleep(): void {
  ensureOverlay()
  bus.on('time:slept', () => {
    lastSleptAt = performance.now()
    startShow()
  })
  bus.on('play:interacted', (e) => {
    if (!bridgeEnabled) return
    if (getPlaceable(e.defId)?.category !== 'bed') return
    // Visiting is READ-ONLY (contract §7). A visitor does not get to put the
    // other kid's town to bed, and does not move their own clock either.
    if (save.visiting) return
    const at = performance.now()
    setTimeout(() => {
      if (at - lastSleptAt < 4000 || performance.now() - lastSleptAt < 4000) return
      bedTapped()
    }, 0)
  })
}
